// Stamp card page controller (UBB-179). Compiled to assets/stamp-card.autojs.js.
// Fetches the logged-in customer's stamp-card progress from the backend via the
// App Proxy and renders the matching Figma state. Guest state is server-rendered.
// Loyalty-page extras (Designs mFgU5IzV2QbEKQPrtJPywq): the kraft meta row
// (progress count + rewards-waiting pill), the hero "X stamps earned" status,
// and the "You are here" pill on the current tier card.

// Fixed slot scheme (Designs 3037:75393 — identical in every state and on the guest card):
// dark gift art marks FUTURE reward slots (1/4/6 small, 9 big) only — once EARNED, a reward
// slot joins the numbered white bells wearing the gift-bow bell (see renderBar). Slot captions
// are merchant-editable (the section's caption_1..caption_10 settings, delivered via the
// data-stamp-captions JSON); DEFAULT_CAPTIONS only backstops a missing/unparsable block.
const REWARD_ART = { 1: 'mugs', 4: 'mugs', 6: 'mugs', 9: 'gift' };
const DEFAULT_CAPTIONS = {
  1: 'Earn free shipping!',
  3: 'Eyes on the prize.',
  4: 'Earn $5 off!',
  6: 'Earn free shipping!',
  8: 'Nearly at the peak!',
  9: 'Earn $10 off!',
};
// Footer CTA fallback threshold — only used when the backend response predates
// rewards_waiting: below this stamp count SHOP & EARN A STAMP, at/above it CLAIM MY
// LOOT. With rewards_waiting present the CTA follows the LIVE unclaimed-reward count
// instead (per UBB-179 direction, 2026-07-31).
const CLAIM_THRESHOLD = 5;

// Hero status copy (Figma dev annotation on node 2913:107802 — "Dynamic Stamp Count").
// FALLBACK ONLY — the live copy is one message per stamp count, authored on the section's
// "Hero message" blocks and delivered in the data-hero-messages JSON. This backstops a
// missing/unparsable block set, or a count the merchant left blank.
// Tracks the SAME reward ladder as DEFAULT_CAPTIONS above — free shipping at 1, $5 off at 4,
// free shipping at 6, $10 off at 9 — so the hero never contradicts the card beneath it. If the
// ladder changes, update both (and the section's Hero message blocks, which win over this).
const DEFAULT_HERO_MESSAGES = {
  0: "Your card's empty, and honestly? That's just sad. One brew unlocks free shipping.",
  1: 'Free shipping, unlocked. Three more brews and $5 off is yours.',
  2: 'Two brews from $5 off. The maths has never been more delicious.',
  3: 'One brew to $5 off. So close you can smell it.',
  4: '$5 off, banked. Two more brews and free shipping comes back around.',
  5: 'One brew to free shipping. Halfway hero, keep going.',
  6: 'Free shipping again, you legend. Three brews to $10 off.',
  7: 'Two brews to $10 off. Certified coffee pro behaviour.',
  8: 'One brew to $10 off. Final sip before glory.',
  9: '$10 off unlocked — Master of the Mug 🏆 People should bow when you enter the kitchen.',
  10: 'Card complete. Claim your loot, then start the whole glorious cycle again.',
};

function heroTierMessage(earned) {
  return pickByCount(DEFAULT_HERO_MESSAGES, earned) || '';
}

function showView(root, name) {
  root.querySelectorAll('[data-stamp-view]').forEach((el) => {
    if (el.dataset.stampView === name) el.setAttribute('data-active', '');
    else el.removeAttribute('data-active');
  });
}

// Resolve a count-keyed map (panel states, hero messages) for the customer's stamp count:
// one entry per count, clamped to the range the merchant actually defined blocks for.
function pickByCount(byCount, earned) {
  const keys = Object.keys(byCount)
    .map(Number)
    .sort((a, b) => a - b);
  if (!keys.length) return undefined;
  const min = keys[0];
  const max = keys[keys.length - 1];
  const level = Math.min(Math.max(earned, min), max);
  return byCount[String(level)] || byCount[String(max)];
}

function renderBar(mount, { slots, earned, captions }) {
  mount.innerHTML = '';
  for (let i = 1; i <= slots; i += 1) {
    const rewardArt = REWARD_ART[i];
    const isEarned = i <= earned;
    const isCurrent = i === earned && earned >= 1;
    const slot = document.createElement('div');
    slot.className = 'stamp-card__slot';
    if (isEarned) slot.classList.add('stamp-card__slot--earned');
    if (rewardArt) slot.classList.add('stamp-card__slot--reward');
    if (isCurrent) slot.classList.add('stamp-card__slot--current');

    // Pod artwork (CSS background per modifier, states sheet 3037:75393): UNEARNED reward
    // slots show the dark gift art with no number; every EARNED slot is a numbered white
    // bell — reward slots wear the gift-BOW bell, plain slots the plain filled bell.
    // Unearned non-reward slots are numbered outline bells.
    const pod = document.createElement('span');
    pod.className = 'stamp-card__pod';
    if (rewardArt && !isEarned) {
      pod.classList.add(`stamp-card__pod--${rewardArt}`);
    } else {
      if (rewardArt) pod.classList.add('stamp-card__pod--gift-earned');
      const num = document.createElement('span');
      num.className = 'stamp-card__num';
      num.textContent = i;
      pod.appendChild(num);
    }
    slot.appendChild(pod);

    // sparkle marks the current (most recently earned) slot
    if (isCurrent) {
      const sparkle = document.createElement('span');
      sparkle.className = 'stamp-card__sparkle';
      sparkle.setAttribute('aria-hidden', 'true');
      slot.appendChild(sparkle);
    }

    const caption = captions[i];
    if (caption) {
      // --captioned drops the sparkle's BOTTOM rays (they'd overlap the label —
      // states sheet: 3/10 and 8/10 show top rays only, 2/10 and 7/10 show both).
      slot.classList.add('stamp-card__slot--captioned');
      const cap = document.createElement('span');
      cap.className = 'stamp-card__caption';
      cap.textContent = caption;
      slot.appendChild(cap);
    }
    mount.appendChild(slot);
  }
}

// Kraft meta row — "6/10  4 brews to Master of the Mug" + the pill. The mint
// reward pill uses the LIVE unclaimed StampCardReward count from the backend
// (rewards_waiting) when present; per-state copy (mint counts are placeholders)
// is the fallback for older backend responses without the field.
function renderMeta(root, { slots, earned, state, rewardsWaiting }) {
  const progress = root.querySelector('[data-stamp-progress]');
  const note = root.querySelector('[data-stamp-progress-note]');
  if (progress) progress.textContent = `${earned}/${slots}`;
  if (note) note.textContent = state.note || '';

  const pill = root.querySelector('[data-stamp-pill]');
  if (!pill) return;
  const hint = state.pill && state.pill.kind === 'hint' ? state.pill : null;
  if (typeof rewardsWaiting === 'number') {
    if (rewardsWaiting > 0) {
      pill.textContent = `🎁 ${rewardsWaiting} reward${rewardsWaiting === 1 ? '' : 's'} waiting`;
      pill.classList.remove('stamp-card__pill--hint');
      pill.hidden = false;
    } else if (hint && hint.text) {
      // no rewards banked — the white nudge pill still applies
      pill.textContent = hint.text;
      pill.classList.add('stamp-card__pill--hint');
      pill.hidden = false;
    } else {
      pill.hidden = true;
    }
    return;
  }
  if (state.pill && state.pill.text) {
    pill.textContent = state.pill.text;
    pill.classList.toggle('stamp-card__pill--hint', state.pill.kind === 'hint');
    pill.hidden = false;
  } else {
    pill.hidden = true;
  }
}

// Footer CTA — CLAIM MY LOOT when the customer actually has unclaimed rewards
// (rewards_waiting > 0), SHOP & EARN A STAMP otherwise. Older backend responses
// without rewards_waiting fall back to the CLAIM_THRESHOLD stamp-count rule.
function renderCta(root, { earned, rewardsWaiting }) {
  const cta = root.querySelector('[data-stamp-cta]');
  if (!cta) return;
  const claiming =
    typeof rewardsWaiting === 'number'
      ? rewardsWaiting > 0
      : earned >= CLAIM_THRESHOLD;
  cta.textContent = claiming ? cta.dataset.claimLabel : cta.dataset.shopLabel;
  cta.setAttribute(
    'href',
    claiming ? cta.dataset.claimHref : cta.dataset.shopHref
  );
}

// Hero status — swaps the member slide's fallback line for the live count + tier message.
// Server renders the fallback copy (Figma annotation) so a fetch failure degrades gracefully.
// `hero` is the merchant's "Hero message" block for this stamp count; a blank or missing
// message falls back to heroTierMessage(). showCount is an opt-OUT that can only HIDE the
// count line — 0 stamps never shows one regardless (Figma annotation case 1: "0 stamps
// earned" above "your card's empty" would state the same thing twice), so a section saved
// before the blocks existed keeps rendering exactly as it does today.
function renderHeroStatus(earned, hero) {
  if (earned <= 0) return;
  const status = document.querySelector(
    '.slideshow-slide .content-block[data-audience="members"]'
  );
  if (!status) return;
  const message = document.createElement('p');
  message.className = 'brand-text body-large';
  message.textContent = (hero && hero.message) || heroTierMessage(earned);
  if ((hero && hero.showCount === false) || earned <= 0) {
    status.replaceChildren(message);
    return;
  }
  const count = document.createElement('p');
  count.className = 'brand-text h3 loyalty-hero__count';
  count.textContent = `${earned} stamp${earned === 1 ? '' : 's'} earned`;
  status.replaceChildren(count, message);
}

// Tier cards — unhide the "You are here" pill on the customer's CURRENT tier
// (highest card threshold ≤ earned; below the first threshold no pill shows).
function renderTierBadge(earned) {
  const badges = Array.from(document.querySelectorAll('[data-tier-threshold]'));
  if (!badges.length) return;
  let current = null;
  badges.forEach((badge) => {
    const at = Number(badge.dataset.tierThreshold) || 0;
    if (
      at > 0 &&
      at <= earned &&
      (!current || at > Number(current.dataset.tierThreshold))
    ) {
      current = badge;
    }
  });
  if (current) current.hidden = false;
}

// Loyalty-card widget reorder (UBB-179): the merchant-added Influence "loyalty card"
// app block renders AFTER the fullpage widget (Shopify block order can't interleave a
// sibling block between the widget's OWN sections), but for members it belongs between
// CLAIM YOUR LOOT and FRIENDS DON'T LET FRIENDS DRINK BAD COFFEE — so we move the
// block's DOM in front of the referral section inside the widget. Anchors: the stable
// #influence-io-referral container + a block-id SUBSTRING (the shopify-block hash
// prefix changes per placement). The widget hydrates async and may re-render its
// section list, so an observer (re)places the held node whenever it drifts — holding
// the reference also survives an innerHTML-style wipe. Guests keep Shopify's order
// (the loot section doesn't render for them).
function initLoyaltyCardReorder() {
  if (!document.body.classList.contains('customer-logged-in')) return;
  const block = document.querySelector(
    '[id*="influence_io_loyalty_rewards_loyalty_card"]'
  );
  if (!block) return;
  const place = () => {
    const referral = document.getElementById('influence-io-referral');
    if (!referral || !referral.parentElement) return;
    if (
      block.parentElement === referral.parentElement &&
      block.nextElementSibling === referral
    )
      return; // already in place — also breaks the observer→insert loop
    referral.parentElement.insertBefore(block, referral);
  };
  const scope = block.closest('.shopify-section') || document.body;
  new MutationObserver(place).observe(scope, {
    childList: true,
    subtree: true,
  });
  place();
}

// "Claim my loot" anchors down to the rewards app widget. The widget's section wrapper id
// embeds a per-theme template id (shopify-section-template--N__…), so a plain hash can't
// target it portably — the template uses the stable hash below and we scroll to the widget.
function bindClaimScroll() {
  document.querySelectorAll('a[href="#claim-your-loot"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const widget = document.querySelector('.LoyaltyFullpage-prefix');
      if (!widget) return;
      e.preventDefault();
      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

async function init() {
  initLoyaltyCardReorder();
  const root = document.querySelector('[data-stamp-card]');
  if (!root) return;
  bindClaimScroll();

  // Guest state is already server-rendered (data-active). Nothing to fetch. Also bail when the
  // logged-in views aren't in the DOM (Liquid rendered the guest branch) even if a customer
  // global exists — otherwise showView() would deactivate the only rendered view.
  const customer = window.UBCustomer;
  if (!customer || !customer.id) return;
  if (!root.querySelector('[data-stamp-view="loading"]')) return;

  const proxyBasePath = root.dataset.proxyBasePath || '/apps/mr-middleware';
  const statesEl = root.querySelector('[data-stamp-states]');
  let states;
  try {
    states = statesEl ? JSON.parse(statesEl.textContent) : {};
  } catch (e) {
    console.warn('stamp-card: states JSON parse failed', e);
    showView(root, 'error');
    return;
  }

  showView(root, 'loading');
  let data;
  try {
    const res = await fetch(`${proxyBasePath}/api/stamp_card`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    data = await res.json();
  } catch (e) {
    console.warn('stamp-card: load failed', e);
    showView(root, 'error');
    return;
  }

  const slots = Number(data.slots) || 0;
  const earned = Math.max(0, Math.min(Number(data.earned) || 0, slots));

  const state = pickByCount(states, earned);
  if (!state) {
    showView(root, 'error');
    return;
  }
  root.querySelector('[data-stamp-heading]').textContent = state.heading || '';
  root.querySelector('[data-stamp-body]').textContent = state.body || '';
  const illo = root.querySelector('[data-stamp-illustration]');
  illo.innerHTML = state.illustration
    ? `<img src="${state.illustration}" alt="" loading="eager" onerror="this.remove()">`
    : '';

  const rewardsWaiting =
    typeof data.rewards_waiting === 'number' && data.rewards_waiting >= 0
      ? data.rewards_waiting
      : undefined;

  // Merchant-edited slot captions (blank = none); built-in defaults if the block is absent.
  let captions = DEFAULT_CAPTIONS;
  const captionsEl = root.querySelector('[data-stamp-captions]');
  if (captionsEl) {
    try {
      captions = JSON.parse(captionsEl.textContent) || DEFAULT_CAPTIONS;
    } catch (e) {
      console.warn('stamp-card: captions JSON parse failed', e);
    }
  }

  // Merchant-edited hero status copy — its OWN block set / payload, keyed by stamp count and
  // independent of the panel states above. Absent or blank entries fall back to heroTierMessage().
  let heroes = {};
  const heroesEl = root.querySelector('[data-hero-messages]');
  if (heroesEl) {
    try {
      heroes = JSON.parse(heroesEl.textContent) || {};
    } catch (e) {
      console.warn('stamp-card: hero messages JSON parse failed', e);
    }
  }

  renderBar(root.querySelector('[data-stamp-bar]'), {
    slots,
    earned,
    captions,
  });
  renderMeta(root, { slots, earned, state, rewardsWaiting });
  renderCta(root, { earned, rewardsWaiting });
  renderHeroStatus(earned, pickByCount(heroes, earned));
  renderTierBadge(earned);

  showView(root, 'loaded');
}

document.addEventListener('DOMContentLoaded', init);
